<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      :settings="settings"
      endpoint="reports/sales/sales-by-vendor"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <el-select
          class="col-span-12 md:col-span-3"
          v-model="filters.vendor_id"
          :placeholder="t('selectVendor')"
          clearable
          filterable
          remote
          :remote-method="searchVendors"
          @change="onFilterChange"
        >
          <el-option
            v-for="v in vendors"
            :key="v.id"
            :label="v.name"
            :value="v.id"
          />
        </el-select>
      </template>

      <template #cards="{ data }">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('itemsSold') }}</div>
              <div class="text-2xl font-bold pt-2">{{ data?.summary?.totalItemsSold ?? 0 }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalRevenue') }}</div>
              <div class="text-2xl font-bold pt-2">{{ formatMoney(data?.summary?.totalRevenue ?? 0, data?.summary?.currency ?? $ctx.currency) }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalCost') }}</div>
              <div class="text-2xl font-bold pt-2">{{ formatMoney(data?.summary?.totalCost ?? 0, data?.summary?.currency ?? $ctx.currency) }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalProfit') }}</div>
              <div class="text-2xl font-bold pt-2">{{ formatMoney(data?.summary?.totalProfit ?? 0, data?.summary?.currency ?? $ctx.currency) }}</div>
            </div>
          </el-card>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useHTTP } from '@/plugins/http'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { ReportsTableSettings } from '@/views/reports/v2/table/utls'
import Vendor from '@/models/vendor/Vendor'

const { t } = useI18n({
  messages: {
    en: {
      salesByVendor: 'Sales by Vendor',
      selectStore: 'Select Store',
      selectVendor: 'Select Vendor',
      item: 'Item',
      totalReceived: 'Total Received',
      currentStock: 'Current Stock',
      totalSold: 'Total Sold',
      salesTotal: 'Sales Total',
      itemCost: 'Item Cost',
      totalCost: 'Total Cost',
      totalProfit: 'Total Profit',
      itemsSold: 'Items Sold',
      totalRevenue: 'Total Revenue',
    },
    ar: {
      salesByVendor: 'المبيعات حسب المورد',
      selectStore: 'اختر المتجر',
      selectVendor: 'اختر المورد',
      item: 'المادة',
      totalReceived: 'الكمية المستلمة',
      currentStock: 'المخزون الحالي',
      totalSold: 'الكمية المباعة',
      salesTotal: 'إجمالي المبيعات',
      itemCost: 'تكلفة المادة',
      totalCost: 'إجمالي التكلفة',
      totalProfit: 'إجمالي الربح',
      itemsSold: 'المواد المباعة',
      totalRevenue: 'إجمالي الإيرادات',
    }
  }
})

const http = useHTTP()
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const settings = ref<Partial<ReportsTableSettings>>({
  hideTable: false,
  customRowsKey: 'items',
  title: t('salesByVendor'),
  hideCards: false,
  hideExport: false,
  hideFilter: false,
  appendQueryParams: {},
  theme: 'ag-theme-balham'
})

const filters = ref({
  vendor_id: ''
})

const vendors = ref<Vendor[]>([])

onBeforeMount(async () => {
  columnDefs.value = [
    {
      headerName: t('item'),
      field: 'itemName',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      minWidth: 250
    },
    {
      headerName: t('currentStock'),
      field: 'currentStock',
      type: 'numericColumn',
      cellStyle: { direction: 'ltr', textAlign: 'center' }
    },
    {
      headerName: t('totalSold'),
      field: 'totalSold',
      type: 'numericColumn',
      cellStyle: { direction: 'ltr', textAlign: 'center' }
    },
    {
      headerName: t('salesTotal'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.salesTotal, data.currency)
      },
      cellStyle: { direction: 'ltr' }
    },
    {
      headerName: t('itemCost'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.itemCost, data.currency)
      },
      cellStyle: { direction: 'ltr' }
    },
    {
      headerName: t('totalCost'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.totalCost, data.currency)
      },
      cellStyle: { direction: 'ltr' }
    },
    {
      headerName: t('totalProfit'),
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return formatMoney(data.totalProfit, data.currency)
      },
      cellStyle: { direction: 'ltr' }
    }
  ]

  await searchVendors('')
})

function onDataLoaded(_data: any) {
}

function onFilterChange() {
  if (!rtable.value) return

  rtable.value.payload = {
    ...rtable.value.payload,
    vendor_id: filters.value.vendor_id || null
  }
  rtable.value.navigate(rtable.value.payload)
}

async function searchVendors(query: string) {
  if (!query) query = ''
  try {
    const res = await http.get<Vendor[]>(`${Vendor.ENDPOINT}?q=${query}`)
    vendors.value = res.map((v) => Vendor.from(v))
  } catch (error) {
    vendors.value = []
  }
}
</script>
